import VectorLayer from 'ol/layer/Vector';
import olMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { all as allStrategy } from 'ol/loadingstrategy';
import Stroke from 'ol/style/Stroke';
import Style, { StyleLike } from 'ol/style/Style';
import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';
import Feature from 'ol/Feature';
import { MapBrowserEvent } from 'ol';
import { GpxRouteStore } from './GpxRouteStore';
import { IGpxFilterCriteria } from './GpxRouteApi';
import { GpxLayerZIndex } from './GpxLayerOrder';
import { normaliseColour } from './MapGpxUtils';

/**
 * Manages the OL VectorLayer that displays the user's GPX routes, fed by our own
 * GeoJSON endpoint (/api/gpxroutes/features) rather than MapServer WFS. The
 * endpoint filters server-side by the active panel search/tags (parameterised —
 * no user text reaches SQL) and returns simplified EPSG:4326 geometry, so the map
 * always matches the list. The `all` loading strategy loads every matching route
 * once per filter change (the set is small and per-user); refresh() re-requests
 * with the current criteria.
 *
 * Feature loading fans out one event per feature, so onFeatureAdded uses
 * upsertSilent + getMutable on the store to avoid a sidebar rebuild per feature;
 * the caller's onUiRebuildNeeded callback is invoked via a debounced rAF once the
 * batch settles.
 */
export class GpxFeatureLayer {

    private layerSource: VectorSource;
    private featureLayer: VectorLayer;
    private uiRebuildTimer: number | null = null;

    public constructor(
        private map: olMap,
        private store: GpxRouteStore,
        private defaultColour: string,
        private onUiRebuildNeeded: () => void,
        private getCriteria: () => IGpxFilterCriteria
    ) {
        this.layerSource = this.createLayerSource();
        this.featureLayer = this.createFeatureLayer();
        this.map.addLayer(this.featureLayer);
    }

    private createLayerSource(): VectorSource {
        // Endpoint returns EPSG:4326; OL reprojects to the 3857 view projection.
        const geoJsonFormat = new GeoJSON({
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
        });
        const source = new VectorSource({
            format: geoJsonFormat,
            strategy: allStrategy,
            url: () => this.buildFeaturesUrl()
        });
        source.on('addfeature', (event) => this.onFeatureAdded(event.feature as Feature));
        return source;
    }

    /** Builds the /features URL carrying the active search/tag filter. */
    private buildFeaturesUrl(): string {
        const criteria = this.getCriteria();
        const params = new URLSearchParams();
        if (criteria.search) {
            params.set('search', criteria.search);
        }
        if (criteria.tags.length > 0) {
            params.set('tags', criteria.tags.join(','));
        }
        const qs = params.toString();
        return '/api/gpxroutes/features' + (qs ? `?${qs}` : '');
    }

    private createFeatureLayer(): VectorLayer {
        return new VectorLayer({
            source: this.layerSource,
            style: (feature) => this.getFeatureStyleForFeature(feature as Feature),
            zIndex: GpxLayerZIndex.routes
        });
    }

    /**
     * Called for every feature returned by the /features endpoint.
     * Uses upsertSilent / getMutable so the sidebar is NOT rebuilt per-feature;
     * scheduleUiRebuild batches the rebuild into a single rAF.
     */
    private onFeatureAdded(feature: Feature): void {
        const gpxGuid = (feature.get('GpxGuid') as string | undefined)?.toLowerCase();
        if (!gpxGuid) {
            return;
        }
        const colourHex = normaliseColour(
            feature.get('ColourHex') as string ?? this.defaultColour,
            this.defaultColour
        );
        const displayName = feature.get('DisplayName') as string | undefined;

        feature.set('gpxGuid', gpxGuid, true);
        if (displayName) {
            feature.set('displayName', displayName, true);
        }

        const existing = this.store.getMutable(gpxGuid);
        if (existing) {
            existing.features.push(feature);
            if (!existing.colourHex) {
                existing.colourHex = colourHex;
            }
            const geom = feature.getGeometry();
            if (geom) {
                if (existing.extent) {
                    olExtent.extend(existing.extent, geom.getExtent());
                } else {
                    existing.extent = geom.getExtent().slice() as olExtent.Extent;
                }
            }
        } else {
            // Feature arrived before the sidebar response (or no sidebar entry
            // for it). Stash a stub so zoom/visibility still work.
            // (LengthMetres comes back as a string property from the JSON; the
            // store only uses it as a fallback, so leave the loose typing.)
            this.store.upsertSilent({
                gpxGuid,
                displayName: displayName ?? gpxGuid,
                colourHex,
                features: [feature],
                extent: feature.getGeometry()?.getExtent().slice() as olExtent.Extent ?? null,
                lengthMetres: feature.get('LengthMetres') as number | undefined,
                isVisible: true
            });
        }

        this.scheduleUiRebuild();
    }

    /**
     * Coalesce many rapid sidebar rebuilds (one per streamed feature) into a single
     * call on the next animation frame.
     */
    private scheduleUiRebuild(): void {
        if (this.uiRebuildTimer !== null) {
            return;
        }
        this.uiRebuildTimer = window.requestAnimationFrame(() => {
            this.uiRebuildTimer = null;
            this.onUiRebuildNeeded();
        });
    }

    /**
     * Refresh the source so newly-created / updated / deleted routes and filter
     * changes are pulled from the server. Clears the stale OL feature references
     * from each store record first so onFeatureAdded can rebuild them.
     */
    public refresh(): void {
        this.store.clearFeatures();
        this.layerSource.refresh();
    }

    /** Force the style function to re-evaluate for all features (e.g. after a visibility change). */
    public invalidateStyles(): void {
        this.featureLayer.changed();
    }

    /** Shows/hides the whole GPX routes layer (hidden while an edit session runs). */
    public setLayerVisible(visible: boolean): void {
        this.featureLayer.setVisible(visible);
    }

    /**
     * The guid of the topmost route line under the event pixel, or null. Mirrors
     * GpxWaypointLayer.findWaypointAtPixel; returns the guid rather than the feature
     * so the layer itself stays private.
     */
    public findRouteAtPixel(evt: MapBrowserEvent): string | null {
        let gpxGuid: string | null = null;
        this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
            gpxGuid = (feature.get('gpxGuid') as string) ?? null;
            return true;
        }, {
            layerFilter: (layer) => layer === this.featureLayer,
            hitTolerance: 5
        });
        return gpxGuid;
    }

    private getFeatureStyleForFeature(feature: Feature): Style[] {
        const gpxGuid = feature.get('gpxGuid') as string | undefined;
        const record = gpxGuid ? this.store.get(gpxGuid) : undefined;
        if (record?.isVisible === false) {
            //an empty style means the feature is never drawn, and OL hit-tests the
            //rendered canvas — so this also makes hidden routes unclickable. Don't
            //"fix" this into a transparent style: findRouteAtPixel would start
            //matching routes the user has hidden.
            return [];
        }
        const colour = normaliseColour(record?.colourHex ?? this.defaultColour, this.defaultColour);
        return this.buildFeatureStyle(colour) as Style[];
    }

    private buildFeatureStyle(darkStrokeColour: string): StyleLike {
        const lightStroke = new Style({
            stroke: new Stroke({
                color: [255, 255, 255, 0.7],
                width: 4,
                lineDash: [4, 8],
                lineDashOffset: 6
            })
        });
        const darkStroke = new Style({
            stroke: new Stroke({
                color: darkStrokeColour,
                width: 4,
                lineDash: [4, 8]
            })
        });
        return [lightStroke, darkStroke];
    }
}
